'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import type { ImageProps } from 'next/image';
import { type ReactNode, useRef } from 'react';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import {
  buildImageTeaserSrc,
  extractImageDimensions,
  extractImageTeaserObjects,
  generateOptimizedImageTeaser,
  renderFallbackImageTeaser,
} from './ImageTeaser.helpers';
import type { ImageTeaserProps } from './types';

const Wrapper = ({
  children,
  trackClick,
  finalLink,
  linkRel,
  linkTarget,
  className,
  style,
}: {
  children: ReactNode;
  trackClick: () => void;
  finalLink: string;
} & Pick<ImageTeaserProps, 'linkRel' | 'linkTarget' | 'className' | 'style'>) => (
  <Link
    href={finalLink}
    onClick={() => trackClick()}
    openInNewTab={!!(linkTarget && linkTarget === '_blank')}
    rel={linkRel === '_none' ? undefined : linkRel}
    className={className}
    style={style}
  >
    {children}
  </Link>
);

/**
 * The ImageTeaser component displays optimized responsive images with art direction support.
 * It uses viewport-based sizing for responsive image selection and getImageProps for optimal
 * image loading. Supports different images for mobile/desktop with automatic browser selection.
 * The component also includes link wrapping with tracking capabilities for campaign analytics.
 *
 * @param {ImageTeaserType} data - The data object containing image URLs for various device types and SEO fields.
 * @param {string} link - The URL to which the image will link.
 * @returns A React component that conditionally renders an optimized image with responsive srcset.
 */
export const ImageTeaser = ({
  data,
  link = '',
  linkTarget,
  linkRel,
  isLazyLoading = false,
  style,
  className,
  sizes,
}: ImageTeaserProps) => {
  const {
    assets: {
      cmsImage: {
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
        imageQuality,
      },
    },
  } = useConfig();

  const imgref = useRef<HTMLImageElement>(null);
  const { trackClick } = useTrackMarketing(data, imgref, link);
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(link);

  const { imageObjDefault, imageObjMobile } = extractImageTeaserObjects(data);
  const altText = data.field_alternate_text ?? '';

  if (!imageObjDefault?.url) {
    return null;
  }

  const { width: defaultWidth, height: defaultHeight } = extractImageDimensions(imageObjDefault);
  const { width: mobileWidth, height: mobileHeight } = extractImageDimensions(imageObjMobile);

  const canOptimizeDefault = !!(defaultWidth && defaultHeight);
  const canOptimizeMobile = !!(mobileWidth && mobileHeight && imageObjMobile?.url);

  const defaultSrc = buildImageTeaserSrc(
    imageObjDefault,
    imageOptimizationEnabled,
    uriTransformSource,
    uriTransformEndpoint,
  );
  const mobileSrc = imageObjMobile
    ? buildImageTeaserSrc(
        imageObjMobile,
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
      )
    : null;

  const commonImageProps: ImageProps = {
    alt: altText,
    loading: isLazyLoading ? 'lazy' : 'eager',
    quality: imageOptimizationEnabled ? imageQuality : undefined,
    unoptimized: imageOptimizationEnabled ? undefined : true,
    fetchPriority: isLazyLoading ? 'auto' : 'high',
    src: '',
  };

  // Wrapper props for conditional link wrapping
  const wrapperProps = {
    trackClick,
    finalLink,
    linkTarget,
    linkRel,
    className,
    style,
  };

  // Fallback for non-optimizable images
  if (!canOptimizeDefault) {
    return (
      <ConditionalWrapper
        condition={finalLink !== ''}
        wrapper={(children) => Wrapper({ children, ...wrapperProps })}
      >
        {renderFallbackImageTeaser(
          imgref,
          defaultSrc,
          altText,
          isLazyLoading ?? false,
          className,
          style,
        )}
      </ConditionalWrapper>
    );
  }

  // Optimized images with art direction and responsive sizing
  const imageElement = generateOptimizedImageTeaser({
    defaultSrc,
    defaultWidth,
    defaultHeight,
    mobileSrc,
    mobileWidth,
    mobileHeight,
    canOptimizeMobile,
    commonImageProps,
    imgref,
    altText,
    className,
    style,
    sizes,
  });

  return (
    <ConditionalWrapper
      condition={finalLink !== ''}
      wrapper={(children) => Wrapper({ children, ...wrapperProps })}
    >
      {imageElement}
    </ConditionalWrapper>
  );
};
